/* A sub's company document -- its MSA, W-9, prequalification, a license, a general certificate -- added (the file,
   then its details) or its details edited. Subcontractor Mgmt picks the kind; a project's Subcontractors tab opens
   it for one paper (Duncan 2026-10-22: PMs upload the MSA and W-9 too), the kind fixed. */
import React, { useRef, useState } from 'react';
import { Icon } from '../ui';
import { ModalShell, Field, Label, ErrorBanner, PrimaryButton, SecondaryButton } from './SubUi';
import { DOC_TYPES, DATE_LABEL, docTypeOf, fmtSize } from '../../lib/subcontractors';

const DAY_RE = /^(19|20)\d{2}-\d{2}-\d{2}$/;
const MAX_UPLOAD_BYTES = 150 * 1024 * 1024; // as the server (Duncan 2026-10-24: a 63 MB safety manual)
const muted = { fontSize: 13, color: 'var(--text-3)' };

export default function CompanyDocDialog({ mode, doc, presetType, lockType = false, subName, initialFile, onClose, onSubmit }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(() => ({
    doc_type: doc?.doc_type || presetType || 'msa',
    title: doc?.title || '',
    number: doc?.number || '',
    signed_date: doc?.signed_date || '',
    effective_date: doc?.effective_date || '',
    expiry_date: doc?.expiry_date || '',
  }));
  const [file, setFile] = useState(initialFile || null); // a file dropped on (or chosen from) a card comes in chosen
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const type = docTypeOf(form.doc_type);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (busy) return;
    if (!isEdit && !file) return setError('Choose the file.');
    if (file && file.size > MAX_UPLOAD_BYTES) return setError('The file is larger than the 150MB limit.');
    for (const k of type.dates) {
      if (form[k] && !DAY_RE.test(form[k])) return setError(`${DATE_LABEL[k]}: pick a date.`);
    }
    if (form.doc_type === 'insurance' && (!form.effective_date || !form.expiry_date)) return setError('An insurance certificate needs its effective and expiration dates.');
    // Only the fields this kind of document has; the others are cleared.
    const details = {
      doc_type: form.doc_type,
      title: form.title.trim(),
      number: type.number ? form.number.trim() : '',
      ...Object.fromEntries(['signed_date', 'effective_date', 'expiry_date'].map((k) => [k, type.dates.includes(k) ? form[k] : ''])),
    };
    setError(null);
    setBusy(true);
    try {
      await onSubmit(details, file);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? `Edit ${doc.title}` : lockType ? `Upload ${type.label}${subName ? ` for ${subName}` : ''}` : 'Add document'}
      subtitle={isEdit ? 'Its details. To change the file, add the new one and remove this.'
        : lockType ? 'A PDF or a photo of it, up to 150MB. It’s the company’s: one for every job.' : 'A PDF or a photo of it, up to 150MB.'}
      onClose={onClose} busy={busy} width={520}
      footer={(
        <>
          <SecondaryButton onClick={() => !busy && onClose()}>Cancel</SecondaryButton>
          <PrimaryButton busy={busy} onClick={submit}>{busy ? (isEdit ? 'Saving…' : 'Uploading…') : isEdit ? 'Save changes' : 'Add document'}</PrimaryButton>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {!lockType && (
          <Field id="doc-type" label="Kind" as="select" value={form.doc_type} onChange={set('doc_type')}
            options={DOC_TYPES.map((t) => ({ value: t.value, label: t.label }))} />
        )}
        {!isEdit && (
          <div>
            <Label required>File</Label>
            <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <SecondaryButton onClick={() => fileRef.current?.click()}><Icon name="upload" size={14} />{file ? 'Choose another' : 'Choose file'}</SecondaryButton>
              <span style={{ ...muted, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file ? `${file.name} · ${fmtSize(file.size)}` : 'No file chosen'}</span>
            </div>
          </div>
        )}
        <Field id="doc-title" label="Title" value={form.title} onChange={set('title')} placeholder={type.title} />
        {type.number && <Field id="doc-number" label={type.number} value={form.number} onChange={set('number')} />}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${type.dates.length}, minmax(0, 1fr))`, gap: 12 }}>
          {type.dates.map((k) => (
            <Field key={k} id={`doc-${k}`} type="date" label={DATE_LABEL[k]} required={form.doc_type === 'insurance' && (k === 'expiry_date' || k === 'effective_date')}
              value={form[k]} onChange={set(k)} />
          ))}
        </div>
        {form.doc_type === 'insurance' && <div style={{ ...muted, marginTop: -6 }}>Expires: the soonest expiry on the certificate.</div>}
      </div>
    </ModalShell>
  );
}
